import { Button } from '@uplink/ui';
import { useEffect, useRef } from 'react';
import { backButton, inTelegram, mainButton } from './telegram.ts';

/** Shows Telegram's BackButton while mounted. Outside Telegram the screen renders its own back link. */
export function useBackButton(onBack: (() => void) | null) {
  const ref = useRef(onBack);
  ref.current = onBack;
  const active = !!onBack;
  useEffect(() => {
    if (!inTelegram || !active) return;
    try {
      backButton.show();
      const off = backButton.onClick(() => ref.current?.());
      return () => {
        off();
        backButton.hide();
      };
    } catch {
      return;
    }
  }, [active]);
}

export interface MainAction {
  text: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}

/**
 * The screen's primary action. Inside Telegram it drives the native MainButton (design system rule);
 * in a browser it renders a fixed bottom bar with the same button.
 */
export function MainActionBar({ action }: { action: MainAction | null }) {
  const ref = useRef(action);
  ref.current = action;

  useEffect(() => {
    if (!inTelegram) return;
    try {
      if (!action) {
        mainButton.setParams({ isVisible: false });
        return;
      }
      mainButton.setParams({
        text: action.text,
        isVisible: true,
        isEnabled: !action.disabled && !action.loading,
        isLoaderVisible: !!action.loading,
      });
    } catch {}
  }, [action?.text, action?.disabled, action?.loading, action]);

  useEffect(() => {
    if (!inTelegram) return;
    try {
      const off = mainButton.onClick(() => ref.current?.onClick());
      return () => {
        off();
        mainButton.setParams({ isVisible: false });
      };
    } catch {
      return;
    }
  }, []);

  if (inTelegram || !action) return null;
  return (
    <div className="ul-bottom-bar">
      <Button size="l" block onClick={action.onClick} disabled={action.disabled} loading={action.loading}>
        {action.text}
      </Button>
    </div>
  );
}
